import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {IFlow} from "../../../../../data/interfaces/iflow.interface";
import {AbstractControl, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {IInputForm} from "../../../../../data/interfaces/iinput-form.interface";
import {ApiFlowDataService} from "../../../../../data/services/api/flow/api-flow-data.service";
import {Subscription} from "rxjs";
import {
  faArrowRight,
  faChevronRight,
  faCircleExclamation,
  faCircleInfo,
  faCheck,
  faCommentSms,
  faRotateRight,
  faShieldHalved,
  faTriangleExclamation,
  faXmark,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import {ESTADOS_LX, EstadoLx} from "../../../../../shared/ui/liga-ui";
import {
  armarGrupos,
  ICampoVista,
  iconoDeTema,
  IGrupoVista,
  TELEFONOS_REFERENCIA,
  textoDuracion,
  textoGuardar
} from "./form-campos";

// Tiempo que se queda el «¡Listo!» antes de regresar solo a la lista (igual que antes).
const ESPERA_REGRESO = 4000

interface IAvanceFormulario {
  total: number
  listos: number
  pct: number
  faltan: number
  siguiente: ICampoVista | null
}

interface IAvisoFormulario {
  titulo: string
  texto: string
}

// Los estilos viven en src/assets/styles/components/_liga-ui.scss y _liga-b.scss
// (el .scss del componente solo trae :host por el tope de 4 kB en producción).
@Component({
  selector: 'app-form-component',
  templateUrl: './form-component.component.html',
  styleUrls: ['./form-component.component.scss']
})
export class FormComponentComponent implements OnInit, OnDestroy {
  @Input() data!: IFlow
  @Input() pushingId: string = ''
  @Output() onEndProcess = new EventEmitter()
  entity!: FormGroup
  inputs: IInputForm[] = []
  dependsOnVisibility: Record<string, boolean> = {}
  private dependsOnSubs: Subscription[] = []
  patterns: any =  {
    phone52: /^[0-9]{10}$/,
    curp: /^[A-Z]{4}[0-9]{2}[0-1][0-9][0-3][0-9][A-Z]{6}[0-9A-Z]{2}$/,
    clabe: /^\d{18}$/,
    email: /^[a-zA-Z0-9_.+]*[a-zA-Z][a-zA-Z0-9_.+]*@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/
  }

  // ---- Vista ----
  readonly estados = ESTADOS_LX
  readonly iconos = {
    error: faCircleExclamation,
    info: faCircleInfo,
    sms: faCommentSms,
    listo: faCheck,
    reintentar: faRotateRight,
    seguro: faShieldHalved,
    aviso: faTriangleExclamation,
    cerrar: faXmark,
    flecha: faArrowRight,
    chevron: faChevronRight
  }
  grupos: IGrupoVista[] = []
  campos: ICampoVista[] = []
  titulo = ''
  subtitulo = ''
  eyebrow = ''
  iconoHero: IconDefinition = iconoDeTema('')
  textoBoton = 'Guardar mi información'
  // Con más de 3 campos el botón va fijo abajo (hay que desplazarse).
  ctaFijo = false
  // Formularios largos: barra «3 de 8 datos listos» y «Siguiente: …».
  mostrarAvance = false
  tieneReferencias = false
  configuracionInvalida = false
  avance: IAvanceFormulario = {total: 0, listos: 0, pct: 0, faltan: 0, siguiente: null}
  conteoGrupo: Record<string, {total: number, listos: number}> = {}
  guardando = false
  guardado = false
  intentoGuardar = false
  mensajeExito = ''
  avisoError: IAvisoFormulario | null = null
  anuncio = ''
  // «Corrige» en el encabezado cuando el paso volvió con motivo de rechazo.
  estadoHero: EstadoLx | null = null
  notaCta = ''

  private subs: Subscription[] = []
  private temporizadorRegreso: ReturnType<typeof setTimeout> | null = null
  private regresoEmitido = false

  constructor(
    private fb: FormBuilder,
    private flowDataService: ApiFlowDataService,
    private host: ElementRef<HTMLElement>
  ) {
  }

  ngOnInit() {
    this.inputs = this.leerConfiguracion()
    this.generateForm(this.inputs)
    this.armarVista()
    this.setupDependsOnListeners()
    this.subs.push(
      this.entity.valueChanges.subscribe(() => this.recalcular()),
      this.entity.statusChanges.subscribe(() => this.recalcular())
    )
    this.recalcular()
  }

  ngOnDestroy() {
    this.dependsOnSubs.forEach(s => s.unsubscribe())
    this.subs.forEach(s => s.unsubscribe())
    this.cancelarRegreso()
  }

  shouldShowField(input: IInputForm): boolean {
    if (input.dependsOn) return this.dependsOnVisibility[input.id] ?? false
    return true
  }

  private setupDependsOnListeners() {
    for (const input of this.inputs) {
      if (!input.dependsOn) continue
      const { field, value } = input.dependsOn
      const controllingCtrl = this.entity.get(field)
      if (!controllingCtrl) continue

      const checkVisibility = (currentValue: string) => {
        const visible = currentValue === value
        this.dependsOnVisibility[input.id] = visible
        this.updateDependsOnValidation(input, visible)
      }

      checkVisibility(controllingCtrl.value)
      const sub = controllingCtrl.valueChanges.subscribe(checkVisibility)
      this.dependsOnSubs.push(sub)
    }
  }

  private updateDependsOnValidation(input: IInputForm, visible: boolean) {
    const ctrl = this.entity.get(input.id)
    if (!ctrl) return
    if (visible) {
      const validators = input.validations
        .filter(v => v.type === 'required')
        .map(() => Validators.required)
      ctrl.setValidators(validators.length ? validators : null)
    } else {
      ctrl.clearValidators()
      ctrl.setValue('')
    }
    ctrl.updateValueAndValidity()
  }

  // Mayúsculas al soltar la tecla (y al salir del campo), como siempre en INPUT_TEXT.
  // Solo reescribe si cambió: así el cursor no brinca al final al corregir en medio.
  validateInput(input: string) {
    const control = this.entity.get(input);
    if (control && typeof control.value === 'string') {
      let cleaned = control.value.toUpperCase()
      if (cleaned !== control.value) control.setValue(cleaned);
    }
  }

  generateForm (inputs: IInputForm[]) {
    const fields:any = {}

    for (const input of inputs) {
      if(input.typeField !== 'SEPARATOR') {
        const validations = []
        for (const validation of input.validations) {
          switch (validation.type) {
            case 'pattern':
              const pattern = this.patterns[validation.value as keyof typeof this.patterns] ?? new RegExp(validation.value.toString())
              validations.push(Validators.pattern(pattern))
              break
            case 'minlength':
              validations.push(Validators.minLength(+validation.value))
              break
            default:
              validations.push(Validators.required)
              break
          }
        }
        if (input.typeField === 'INPUT_NUMBER') fields[input.id] = [0, validations]
        else fields[input.id] = ['', validations]
      }

    }
    this.entity = this.fb.group(fields)
  }

  private applyServerErrors(errorsByField: Record<string, string>) {
  if (!errorsByField) return;

  Object.entries(errorsByField).forEach(([field, msg]) => {
    const ctrl = this.entity.get(field);
    if (ctrl) {
      // updateValueAndValidity vuelve a correr los validadores y borraba el error
      // del servidor: ahora va primero y el mensaje se queda en el campo.
      ctrl.updateValueAndValidity({ emitEvent: false });
      const prev = ctrl.errors || {};
      ctrl.setErrors({ ...prev, custom: msg || 'Campo inválido' });
      ctrl.markAsTouched();
      ctrl.markAsDirty();
    } else {
      console.warn('[applyServerErrors] No se encontró FormControl para', field);
    }
  });
}

  // Botón principal: si falta algo no se manda nada; se marca en línea y se
  // lleva al usuario al primer dato por corregir.
  guardar() {
    if (this.guardando || this.guardado || this.configuracionInvalida) return
    this.avisoError = null
    this.intentoGuardar = true
    this.entity.markAllAsTouched()
    this.revisarTelefonosRepetidos()
    this.recalcular()
    if (this.entity.invalid) {
      const pendientes = this.camposInvalidos().length
      this.anuncio = pendientes === 1 ? 'Revisa el dato marcado en rojo.' : `Revisa los ${pendientes} datos marcados en rojo.`
      this.enfocarPrimerInvalido()
      return
    }
    this.save()
  }

save() {
  this.guardando = true;

    this.flowDataService
    .saveInfo({
      data: JSON.stringify(this.entity.value),
      idDocument: this.data.id,
      idStage: this.data.userDocumentId,
      pushingId: this.pushingId
    })
    .subscribe({
      next: (r) => {
        this.guardando = false;
        Object.keys(this.entity.controls).forEach(k => {
          const c = this.entity.get(k);
          c?.setErrors(null);
          c?.markAsPristine();
        });

        if (!r.error) {
          this.alGuardar(r.msg)
        } else {
          const conCampos = this.marcarTelefonosDelServidor(r.msg)
          this.mostrarError('No pudimos guardar tu información', r.msg || 'Inténtalo de nuevo en un momento.', conCampos)
        }
      },
      error: (err) => {
        this.guardando = false;
        const plainMsg =
          err?.error?.msg ||
          err?.message ||
          'Error al guardar la información.';
        const legacyFields = err?.error?.data?.msg?.fields || err?.error?.msg?.fields;
        if (legacyFields) {
          this.applyServerErrors(legacyFields);
        }
        this.mostrarError('Revisa tus datos', String(plainMsg), !!legacyFields)
      }
    });
}

  continuar() {
    this.cancelarRegreso()
    this.emitirRegreso()
  }

  // Configuración ilegible: en vez de una pantalla en blanco, regresa a la lista
  // (que vuelve a pedir el flujo).
  volverALista() {
    this.continuar()
  }

  cerrarAviso() {
    this.avisoError = null
  }

  // ---- Ayudas de la plantilla ----

  invalido(campo: ICampoVista): boolean {
    const control = this.entity.get(campo.id)
    return !!control && control.invalid && (control.touched || this.intentoGuardar)
  }

  mensajeError(campo: ICampoVista): string {
    const errores = this.entity.get(campo.id)?.errors
    if (!errores) return ''
    if (errores['custom']) return String(errores['custom'])
    if (errores['repetido']) return 'Ya usaste este número en otra referencia. Escribe uno distinto.'
    const validaciones = campo.input.validations ?? []
    const configurado = (tipos: string[]) => validaciones.find(v => tipos.includes(v.type) && v.errorMsg)?.errorMsg ?? ''
    if (errores['required']) {
      return configurado(validaciones.filter(v => !['pattern', 'minlength'].includes(v.type)).map(v => v.type))
        || (['lista', 'opciones'].includes(campo.tipo) ? 'Elige una opción.' : campo.tipo === 'fecha' ? 'Elige una fecha.' : 'Este dato es obligatorio.')
    }
    // El panel genera «<campo> no tiene un formato válido» si nadie escribe otro
    // mensaje: en ese caso se dice cómo corregirlo.
    if (errores['pattern']) {
      const propio = configurado(['pattern'])
      return propio && !/no tiene un formato v[aá]lido\.?$/i.test(propio) ? propio : this.mensajeFormato(campo)
    }
    if (errores['minlength']) {
      return configurado(['minlength']) || `Escribe al menos ${errores['minlength'].requiredLength} caracteres.`
    }
    return 'Revisa este dato.'
  }

  largo(campo: ICampoVista): number {
    const valor = this.entity.get(campo.id)?.value
    return valor === null || valor === undefined ? 0 : String(valor).length
  }

  tienePie(campo: ICampoVista): boolean {
    return !!campo.ayuda || campo.contador || this.invalido(campo)
  }

  grupoVisible(grupo: IGrupoVista): boolean {
    return grupo.campos.some(c => this.shouldShowField(c.input))
  }

  elegida(campo: ICampoVista, valor: string): boolean {
    return this.entity.get(campo.id)?.value === valor
  }

  // Al soltar una tecla: mayúsculas en los campos de texto, como antes.
  alSoltarTecla(campo: ICampoVista) {
    if (campo.mayusculas) this.validateInput(campo.id)
  }

  alSalir(campo: ICampoVista) {
    if (campo.mayusculas) this.validateInput(campo.id)
  }

  // CLABE, CP y otros números: fuera espacios y guiones (también al pegar).
  // Lee el valor del evento: este (input) puede correr antes que el del FormControl.
  alEscribir(campo: ICampoVista, evento: Event) {
    if (!campo.soloDigitos) return
    const control = this.entity.get(campo.id)
    const escrito = (evento.target as HTMLInputElement | null)?.value
    if (!control || typeof escrito !== 'string') return
    let limpio = escrito.replace(/\D/g, '')
    if (campo.maxLength) limpio = limpio.slice(0, campo.maxLength)
    if (limpio !== escrito) control.setValue(limpio)
  }

  // Números (arrancan en 0): al entrar se selecciona todo para escribir encima.
  seleccionarTodo(evento: Event) {
    const input = evento?.target as HTMLInputElement | null
    if (input && typeof input.select === 'function') setTimeout(() => input.select())
  }

  enfocar(campo: ICampoVista) {
    const raiz = this.host.nativeElement
    const destino = raiz.querySelector<HTMLElement>(`#${campo.domId}`)
    if (!destino) return
    const enfocable = destino.matches('input, select, textarea, button')
      ? destino
      : destino.querySelector<HTMLElement>('input, select, textarea, button') ?? destino
    const caja = destino.closest('.lx-campo') ?? destino
    caja.scrollIntoView({block: 'center', behavior: this.movimientoReducido() ? 'auto' : 'smooth'})
    enfocable.focus({preventScroll: true})
  }

  porClave(_: number, grupo: IGrupoVista) {
    return grupo.clave
  }

  porId(_: number, campo: ICampoVista) {
    return campo.id
  }

  // ---- Internos ----

  private leerConfiguracion(): IInputForm[] {
    try {
      const inputs = JSON.parse(this.data?.properties?.settings ?? '')
      if (Array.isArray(inputs)) return inputs.map(i => ({...i, validations: Array.isArray(i?.validations) ? i.validations : []}))
    } catch {
      // Se avisa en pantalla (configuracionInvalida).
    }
    this.configuracionInvalida = true
    return []
  }

  private armarVista() {
    this.grupos = armarGrupos(this.inputs)
    this.campos = this.grupos.reduce((todos, g) => todos.concat(g.campos), [] as ICampoVista[])
    this.titulo = this.data?.properties?.title || 'Tu información'
    this.iconoHero = iconoDeTema(`${this.titulo} ${this.data?.properties?.name ?? ''}`)
    this.textoBoton = textoGuardar(this.titulo)
    this.ctaFijo = this.campos.length > 3
    this.mostrarAvance = this.campos.filter(c => c.obligatorio).length >= 4
    this.tieneReferencias = this.campos.some(c => TELEFONOS_REFERENCIA.includes(c.id))
    this.eyebrow = this.campos.length ? textoDuracion(this.campos.length) : 'Formulario'
    const hayOpcionales = this.campos.some(c => !c.obligatorio)
    this.estadoHero = this.data?.comment ? 'corrige' : null
    this.subtitulo = this.data?.properties?.subtitle
      || (hayOpcionales ? 'Llena tus datos. Lo que diga «(opcional)» puedes dejarlo en blanco.' : 'Llena todos los datos para continuar.')
  }

  // Avance real: obligatorios visibles que ya están bien llenos.
  private recalcular() {
    const conteo: Record<string, {total: number, listos: number}> = {}
    let total = 0
    let listos = 0
    let siguiente: ICampoVista | null = null
    for (const grupo of this.grupos) {
      const c = {total: 0, listos: 0}
      for (const campo of grupo.campos) {
        if (!campo.obligatorio || !this.shouldShowField(campo.input)) continue
        c.total++
        if (this.estaListo(campo)) c.listos++
        else if (!siguiente) siguiente = campo
      }
      conteo[grupo.clave] = c
      total += c.total
      listos += c.listos
    }
    this.conteoGrupo = conteo
    this.avance = {
      total,
      listos,
      faltan: total - listos,
      pct: total ? Math.round(listos * 100 / total) : 100,
      siguiente
    }
    const faltan = total - listos
    this.notaCta = !total ? 'Todos los datos son opcionales.'
      : !faltan ? 'Todo listo para guardar.'
      : faltan === 1 ? 'Te falta 1 dato obligatorio.'
      : `Te faltan ${faltan} datos obligatorios.`
  }

  private estaListo(campo: ICampoVista): boolean {
    const control = this.entity.get(campo.id)
    if (!control || control.invalid) return false
    const valor = control.value
    return !(valor === null || valor === undefined || valor === '')
  }

  private camposInvalidos(): ICampoVista[] {
    return this.campos.filter(c => this.shouldShowField(c.input) && !!this.entity.get(c.id)?.invalid)
  }

  // El backend rechaza referencias con el mismo teléfono; se avisa antes de mandar.
  private revisarTelefonosRepetidos() {
    const vistos = new Set<string>()
    // Lo marcado en un intento anterior se vuelve a calcular (quizá ya corrigió el otro).
    for (const campo of this.campos) {
      const control = this.entity.get(campo.id)
      if (TELEFONOS_REFERENCIA.includes(campo.id) && control?.errors?.['repetido']) control.updateValueAndValidity()
    }
    for (const campo of this.campos) {
      if (!TELEFONOS_REFERENCIA.includes(campo.id) || !this.shouldShowField(campo.input)) continue
      const control: AbstractControl | null = this.entity.get(campo.id)
      const valor = (control?.value ?? '').toString().replace(/\D/g, '')
      if (!control || valor.length < 10) continue
      if (vistos.has(valor)) control.setErrors({...(control.errors ?? {}), repetido: true})
      vistos.add(valor)
    }
  }

  // El backend responde en texto qué referencia choca («Referencia laboral (55…)
  // coincide con…»): se marca ese celular para que sepa cuál cambiar.
  private marcarTelefonosDelServidor(msg: string): boolean {
    const etiquetas: Record<string, string> = {
      personal_reference_phone: 'Referencia personal',
      work_reference_phone: 'Referencia laboral',
      other_reference_phone: 'Otra referencia'
    }
    let marcados = 0
    for (const id of TELEFONOS_REFERENCIA) {
      const control = this.entity.get(id)
      const campo = this.campos.find(c => c.id === id)
      if (!control || !campo || !this.shouldShowField(campo.input)) continue
      if (!(msg ?? '').startsWith(etiquetas[id] + ' (') && !(msg ?? '').includes('. ' + etiquetas[id] + ' (')) continue
      control.setErrors({...(control.errors ?? {}), custom: 'Este número coincide con otro que ya registraste. Escribe uno distinto.'})
      control.markAsTouched()
      marcados++
    }
    return marcados > 0
  }

  private mensajeFormato(campo: ICampoVista): string {
    const largo = this.largo(campo)
    const faltan = (total: number, singular: string, plural: string) => {
      const n = total - largo
      if (n <= 0) return ''
      return n === 1 ? `Te falta 1 ${singular}` : `Te faltan ${n} ${plural}`
    }
    switch (campo.tipo) {
      case 'telefono': {
        const f = faltan(10, 'dígito', 'dígitos')
        return f ? `${f}.` : 'Revisa el número: lleva 10 dígitos.'
      }
      case 'correo': return 'Revisa el correo: debe verse como nombre@correo.com.'
      case 'clabe': {
        const f = faltan(18, 'dígito', 'dígitos')
        return f ? `${f}: la CLABE lleva 18.` : 'Revisa la CLABE: lleva 18 dígitos.'
      }
      case 'curp': {
        const f = faltan(18, 'carácter', 'caracteres')
        return f ? `${f}: la CURP lleva 18.` : 'Revisa la CURP: cópiala tal como viene en tu INE.'
      }
      case 'numerico': {
        if (!campo.contador || !campo.maxLength) return 'Escribe solo números.'
        const f = faltan(campo.maxLength, 'dígito', 'dígitos')
        return f ? `${f}.` : `Revisa el número: son ${campo.maxLength} dígitos.`
      }
      default: return 'Revisa cómo lo escribiste; hay algo que no coincide con el formato.'
    }
  }

  private enfocarPrimerInvalido() {
    setTimeout(() => {
      const primero = this.camposInvalidos()[0]
      if (primero) this.enfocar(primero)
    })
  }

  private alGuardar(msg: string) {
    this.guardado = true
    this.mensajeExito = msg || 'Tu información quedó registrada.'
    this.anuncio = '¡Listo! Guardamos tu información.'
    setTimeout(() => window.scrollTo({top: 0, behavior: this.movimientoReducido() ? 'auto' : 'smooth'}))
    this.cancelarRegreso()
    this.temporizadorRegreso = setTimeout(() => this.emitirRegreso(), ESPERA_REGRESO)
  }

  private mostrarError(titulo: string, texto: string, conCampos: boolean) {
    // role="alert" ya lo anuncia; aquí no se repite.
    this.avisoError = {titulo, texto}
    if (conCampos) return this.enfocarPrimerInvalido()
    setTimeout(() => {
      const aviso = this.host.nativeElement.querySelector<HTMLElement>('.lxb-aviso-error')
      if (!aviso) return
      aviso.scrollIntoView({block: 'center', behavior: this.movimientoReducido() ? 'auto' : 'smooth'})
      aviso.focus({preventScroll: true})
    })
  }

  private emitirRegreso() {
    this.temporizadorRegreso = null
    if (this.regresoEmitido) return
    this.regresoEmitido = true
    this.onEndProcess.emit(true)
  }

  private cancelarRegreso() {
    if (this.temporizadorRegreso) clearTimeout(this.temporizadorRegreso)
    this.temporizadorRegreso = null
  }

  private movimientoReducido(): boolean {
    return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }
}
